import { trackGa4Checkout } from '../../lib/ga4';
import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useUserContext } from '../../contexts';
import { subscribeUserFn } from '../../lib/api';
import { Modal } from '../Modal';
import { Spinner } from '../Spinner';

const normalizePeriodLabel = (value) => {
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    return normalizedValue.startsWith('year') ? 'year' : 'month';
};

const SubscriptionUpgradeModals = forwardRef((props, ref) => {
    const { user } = useUserContext();
    const [confirmation, setConfirmation] = useState(null);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [paymentMethodUrl, setPaymentMethodUrl] = useState(null);

    const startUpgrade = useCallback(async (planId) => {
        if (!planId) {
            return;
        }

        const provider = user?.team?.subscription?.paypal ? 'paypal' : 'stripe';
        setConfirmation(null);
        setIsUpgrading(true);

        try {
            const data = await subscribeUserFn({ provider, planId });
            setIsUpgrading(false);

            if (data?.url && data.type === 'setup') {
                setPaymentMethodUrl(data.url);
                return;
            }

            if (data?.url && data.type !== 'success') {
                trackGa4Checkout(data.analytics);
                document.location.href = data.url;
                return;
            }

            if (data?.type === 'success') {
                setShowSuccess(true);
            }
        } catch (error) {
            console.log('Problem while upgrading subscription', error);
            setIsUpgrading(false);
        }
    }, [user?.team?.subscription?.paypal]);

    useImperativeHandle(ref, () => ({
        confirmUpgrade({ planId, fromPrice, toPrice, periodicityType }) {
            setConfirmation({
                planId,
                fromPrice,
                toPrice,
                periodLabel: normalizePeriodLabel(periodicityType),
            });
        },
        startUpgrade,
    }), [startUpgrade]);

    return (
        <>
            <Modal
                visible={!!confirmation}
                title="Confirm Your Plan Upgrade"
                msg={confirmation && (
                    <div style={{ textAlign: 'left' }}>
                        Please review the details of the changes to your billing:
                        <br />
                        <ul style={{ listStyleType: 'disc', marginTop: '12px', listStylePosition: 'inside' }}>
                            <li>
                                You're moving from our ${confirmation.fromPrice}/{confirmation.periodLabel} plan to the ${confirmation.toPrice}/{confirmation.periodLabel} plan.
                            </li>
                        </ul>
                        <br />
                        If you have any questions about your upgrade or billing details, please reach out to our support team at{' '}
                        <a href="mailto:support@productioncrate.com">Support@ProductionCrate.com</a>.
                    </div>
                )}
                cta="Upgrade"
                ctaCallback={() => startUpgrade(confirmation?.planId)}
                ctaClose="Cancel"
                ctaCloseCallback={() => setConfirmation(null)}
                overlayCallback={() => setConfirmation(null)}
                style={{ height: 'max-content' }}
            />
            <Modal
                visible={isUpgrading}
                title="Upgrade in process"
                style={{ height: 'max-content' }}
                msg={(
                    <div>
                        <Spinner style={{ position: 'relative' }} label={false} />
                        Please wait...
                    </div>
                )}
                cta={false}
            />
            <Modal
                visible={showSuccess}
                title="Upgrade Successful!"
                img="https://cdn.staticcrate.com/stock-hd/graphics/print-design/proplan-illustration-01_prev_sm.2.png"
                msg="Thanks! You have successfully upgraded your account. Keep downloading great assets and making it awesome!"
                cta="Continue"
                ctaCallback={() => {
                    document.location.href = '/account';
                }}
                style={{ height: 'max-content' }}
            />
            <Modal
                visible={!!paymentMethodUrl}
                title="Upgrade failed"
                msg={(
                    <div style={{ textAlign: 'left' }}>
                        Your payment method was declined. Please update it to <b>proceed with the upgrade</b>.
                    </div>
                )}
                cta="Update Payment Method"
                ctaSize="small"
                ctaCallback={() => {
                    document.location.href = paymentMethodUrl;
                }}
                ctaClose="Cancel"
                ctaCloseCallback={() => setPaymentMethodUrl(null)}
                overlayCallback={() => setPaymentMethodUrl(null)}
                style={{ height: 'max-content' }}
            />
        </>
    );
});

export default SubscriptionUpgradeModals;
